<template>
  <div class="bulk-product-upload">
    <div class="page-header" v-if="!selectedUpload">
      <h2>Bulk Product Upload</h2>
      <div class="header-actions">
        <button class="btn-info-button" @click="showUpload = true">
          Upload new file
        </button>
        <button class="btn-dark-button" @click="refreshUploads">
          Refresh
        </button>
      </div>
    </div>

    <UploadComponent
      v-model="showUpload"
      :allowedTypes="['bulk_product_create']"
      @uploadedFile="onFileUploaded"
    />

    <!-- Upload history -->
    <div v-if="!selectedUpload">
      <div v-if="loadingUploads" class="loading-box">
        <BaseSmallSpinner />
      </div>
      <div v-else-if="uploads.length === 0" class="empty-state">
        No bulk product uploads yet. Click "Upload new file" to start.
      </div>
      <table v-else class="default-table">
        <thead>
          <tr class="table100-head">
            <th>File</th>
            <th>Date</th>
            <th>Status</th>
            <th>Rows</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="upload in uploads" :key="upload.id">
            <td>{{ upload.fileName }}</td>
            <td>{{ upload.date | dateWithHours }}</td>
            <td>
              <span :class="['status-badge', upload.status]">{{ upload.status }}</span>
            </td>
            <td>{{ upload.validRows || 0 }} valid / {{ upload.invalidRows || 0 }} invalid</td>
            <td>
              <button class="btn-info-button btn-sm" @click="openUpload(upload)">
                Review
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Upload detail / review panel -->
    <div v-else class="review-panel">
      <div class="review-header">
        <button class="btn-dark-button btn-sm" @click="$router.push({ name: 'BulkProductUpload' })">
          ← Back
        </button>
        <span class="review-title">{{ selectedUpload.fileName }}</span>
        <div class="review-actions">
          <button class="btn-dark-button btn-sm" @click="openGenerateCodes">
            Generate NKR Codes
          </button>
          <button class="btn-dark-button btn-sm" @click="revalidate" :disabled="validating">
            {{ validating ? 'Validating...' : 'Validate' }}
          </button>
          <button
            class="btn-info-button btn-sm"
            @click="commitUpload"
            :disabled="!counts || counts.valid === 0"
          >
            Commit ({{ counts ? counts.valid : 0 }} products)
          </button>
        </div>
      </div>

      <!-- Tabs: separan lo que se va a crear (Valid) de lo que hay que corregir
           (Errors) — se puede descargar cada sección aparte para arreglar afuera
           y volver a subir solo esas filas. -->
      <div class="review-tabs" v-if="counts">
        <button
          class="review-tab"
          :class="{ active: activeFilter === null }"
          @click="activeFilter = null"
        >
          All <span class="tab-count">{{ counts.total }}</span>
        </button>
        <button
          class="review-tab tab-valid"
          :class="{ active: activeFilter === true }"
          @click="activeFilter = true"
        >
          Valid <span class="tab-count">{{ counts.valid }}</span>
        </button>
        <button
          class="review-tab tab-invalid"
          :class="{ active: activeFilter === false }"
          @click="activeFilter = false"
        >
          Errors <span class="tab-count">{{ counts.invalid }}</span>
        </button>
        <button
          class="btn-dark-button btn-sm download-btn"
          @click="downloadCsv"
          :disabled="downloadingCsv || !counts || counts.total === 0"
          :title="activeFilter === null ? 'Download all rows as CSV' : activeFilter === true ? 'Download valid rows as CSV' : 'Download rows with errors as CSV'"
        >
          {{ downloadingCsv ? 'Downloading...' : '⬇ Download CSV' }}
        </button>
      </div>

      <!-- Resumen de errores agrupados: para saber qué corregir primero sin
           tener que leer fila por fila entre miles de filas. -->
      <div v-if="activeFilter === false && errorSummary.length" class="error-summary">
        <div class="error-summary-title">
          Errors breakdown ({{ errorSummary.length }} distinct)
          <button v-if="errorFilter" class="clear-filter-btn" @click="clearErrorFilter">✕ clear filter</button>
        </div>
        <ul>
          <li
            v-for="item in errorSummary"
            :key="item.error"
            :class="{ active: errorFilter === item.error }"
            @click="filterByError(item.error)"
          >
            <span class="error-count">{{ item.count }}</span> {{ item.error }}
          </li>
        </ul>
      </div>

      <div v-if="errorFilter" class="active-error-filter">
        Showing only rows with: <strong>{{ errorFilter }}</strong>
        <button class="clear-filter-btn" @click="clearErrorFilter">✕ clear</button>
      </div>

      <div v-if="loadingRows" class="loading-box">
        <BaseSmallSpinner />
      </div>
      <div v-else-if="rows.length === 0" class="empty-state">
        No rows match the current filter.
      </div>
      <div v-else class="rows-table-wrap">
        <table class="default-table rows-table">
          <thead>
            <tr class="table100-head">
              <th>#</th>
              <th>NKR Code</th>
              <th>
                Brand
                <div class="th-bulk">
                  <select class="inline-select" v-model="bulkBrand">
                    <option value="">—</option>
                    <option v-for="b in brands" :key="b.id" :value="b.name">{{ b.name }}</option>
                  </select>
                  <button class="btn-dark-button btn-xs" @click="applyBulkBrand" :disabled="!bulkBrand">✓</button>
                </div>
              </th>
              <th>Description</th>
              <th>List Price</th>
              <th>Cost</th>
              <th>OE</th>
              <th>Unificado</th>
              <th>
                Dependencia
                <div class="th-bulk">
                  <select class="inline-select" v-model="bulkDependencia">
                    <option value="">—</option>
                    <option value="A">A</option>
                    <option value="B2">B2</option>
                    <option value="Z">Z</option>
                  </select>
                  <button class="btn-dark-button btn-xs" @click="applyBulkDependencia" :disabled="!bulkDependencia">✓</button>
                </div>
              </th>
              <th>
                Price Group
                <div class="th-bulk">
                  <select class="inline-select" v-model="bulkPriceGroup">
                    <option value="">—</option>
                    <option v-for="pg in priceGroups" :key="pg.id" :value="pg.name">{{ pg.name }}</option>
                  </select>
                  <button class="btn-dark-button btn-xs" @click="applyBulkPriceGroup" :disabled="!bulkPriceGroup">✓</button>
                </div>
              </th>
              <th title="Only relevant for price_group = LP45. Check if the cost you entered already has the dealer profit applied (e.g. a landed cost from the supplier) — the system will divide it back out so list_price isn't double-counted.">
                Cost incl.<br />dealer profit
                <div class="th-bulk">
                  <select class="inline-select" v-model="bulkCostIncludesDealerProfit">
                    <option value="">—</option>
                    <option value="1">Yes</option>
                    <option value="0">No</option>
                  </select>
                  <button class="btn-dark-button btn-xs" @click="applyBulkCostIncludesDealerProfit" :disabled="bulkCostIncludesDealerProfit === ''">✓</button>
                </div>
              </th>
              <th title="Matched against application_options_category_v2.category_url.">
                Categoria URL
              </th>
              <th title="Comma or | separated sub_category_url(s), matched under Categoria URL against application_options_subcategory_v2.sub_category_url.">
                Subcategoria URL
              </th>
              <th title="Comma or | separated sub_category_url(s) under the fixed 'market' category. Assigned as category relations on commit, same mechanism as Categoria/Subcategoria URL.">
                Market
              </th>
              <th>Superseded Parts</th>
              <th title="Optional. Saved to part_info_titles.title_alt on commit; description is used as the main title.">
                Alt Title
              </th>
              <th>Status</th>
              <th>Errors</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in rows"
              :key="row.id"
              :class="row.isValid ? 'row-valid' : 'row-invalid'"
            >
              <td>{{ row.rowIndex + 1 }}</td>
              <td>
                <input
                  class="inline-input"
                  v-model="row.nkrCode"
                  @blur="saveRow(row, 'nkr_code', row.nkrCode)"
                  placeholder="e.g. ABC0001"
                />
              </td>
              <td>
                <input
                  class="inline-input"
                  v-model="row.brand"
                  @blur="saveRow(row, 'brand', row.brand)"
                />
              </td>
              <td>
                <input
                  class="inline-input wide"
                  v-model="row.description"
                  @blur="saveRow(row, 'description', row.description)"
                />
              </td>
              <td>
                <input
                  class="inline-input narrow"
                  v-model="row.listPrice"
                  @blur="saveRow(row, 'list_price', row.listPrice)"
                  type="number"
                />
              </td>
              <td>
                <input
                  class="inline-input narrow"
                  v-model="row.cost"
                  @blur="saveRow(row, 'cost', row.cost)"
                  type="number"
                />
              </td>
              <td>
                <input
                  class="inline-input"
                  v-model="row.oe"
                  @blur="saveRow(row, 'oe', row.oe)"
                  placeholder="OE..."
                />
              </td>
              <td>
                <input
                  class="inline-input"
                  v-model="row.unificado"
                  @blur="saveRow(row, 'unificado', row.unificado)"
                  placeholder="Unificado..."
                />
              </td>
              <td>
                <select
                  class="inline-select"
                  v-model="row.dependencia"
                  @change="saveRow(row, 'dependencia', row.dependencia)"
                >
                  <option value="A">A</option>
                  <option value="B2">B2</option>
                  <option value="Z">Z</option>
                </select>
              </td>
              <td>
                <select
                  class="inline-select"
                  v-model="row.priceGroup"
                  @change="saveRow(row, 'price_group', row.priceGroup)"
                >
                  <option value="">—</option>
                  <option v-for="pg in priceGroups" :key="pg.id" :value="pg.name">{{ pg.name }}</option>
                </select>
              </td>
              <td class="text-center">
                <input
                  type="checkbox"
                  :disabled="row.priceGroup !== 'LP45'"
                  :checked="!!row.costIncludesDealerProfit"
                  :title="
                    row.priceGroup !== 'LP45'
                      ? 'Only relevant for price_group = LP45'
                      : 'Cost already includes the dealer profit (e.g. supplier landed cost) — will be divided back out before saving'
                  "
                  @change="
                    row.costIncludesDealerProfit = $event.target.checked;
                    saveRow(
                      row,
                      'cost_includes_dealer_profit',
                      $event.target.checked ? 1 : 0
                    );
                  "
                />
              </td>
              <td>
                <input
                  class="inline-input"
                  v-model="row.categoriaUrl"
                  @blur="saveRow(row, 'categoria_url', row.categoriaUrl)"
                  placeholder="e.g. brakes"
                />
              </td>
              <td>
                <input
                  class="inline-input"
                  v-model="row.subcategoriaUrl"
                  @blur="saveRow(row, 'subcategoria_url', row.subcategoriaUrl)"
                  placeholder="e.g. pads, rotors"
                />
              </td>
              <td>
                <input
                  class="inline-input"
                  v-model="row.market"
                  @blur="saveRow(row, 'market', row.market)"
                  placeholder="e.g. USA, MEXICO"
                />
              </td>
              <td>
                <input
                  class="inline-input wide"
                  v-model="row.supersededParts"
                  @blur="saveRow(row, 'superseded_parts', row.supersededParts)"
                  placeholder="CODE1, CODE2, ..."
                />
              </td>
              <td>
                <input
                  class="inline-input wide"
                  v-model="row.titleAlt"
                  @blur="saveRow(row, 'title_alt', row.titleAlt)"
                />
              </td>
              <td>
                <span :class="row.isValid ? 'pill-valid' : 'pill-invalid'">
                  {{ row.isValid ? 'Valid' : 'Invalid' }}
                </span>
              </td>
              <td class="errors-cell">
                <span v-if="row.errors && row.errors.length" class="error-list">
                  {{ formatRowErrors(row.errors) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="pagination-bar" v-if="pagination && pagination.totalPages > 1">
          <button class="btn-dark-button btn-sm" @click="goToPage(page - 1)" :disabled="page <= 1">‹ Prev</button>
          <span>Page {{ pagination.page }} of {{ pagination.totalPages }} ({{ pagination.total }} rows)</span>
          <button class="btn-dark-button btn-sm" @click="goToPage(page + 1)" :disabled="page >= pagination.totalPages">Next ›</button>
        </div>
      </div>
    </div>

    <!-- Generate codes modal -->
    <BaseModal v-model="showGenerateCodes" class="generate-codes-modal">
      <div class="generate-modal">
        <h3>Generate NKR Codes</h3>
        <p>Enter a 3-character prefix. Codes will be assigned to rows without an nkr_code.</p>
        <div class="generate-form">
          <label>Prefix (3 chars)</label>
          <input
            class="default-input"
            v-model="codePrefix"
            maxlength="3"
            placeholder="ABC"
            style="text-transform:uppercase"
            @input="codePrefix = codePrefix.toUpperCase()"
          />
          <label>Start from</label>
          <input class="default-input" v-model.number="codeStartFrom" type="number" min="1" />
        </div>
        <div class="preview" v-if="codePrefix.length >= 1">
          Preview: {{ codePrefix.toUpperCase().padEnd(3,'_').slice(0,3) }}-{{ String(codeStartFrom).padStart(4, '0') }}
        </div>
        <button
          class="btn-info-button"
          @click="runGenerateCodes"
          :disabled="codePrefix.length < 1 || generatingCodes"
        >
          {{ generatingCodes ? 'Generating...' : 'Generate' }}
        </button>
      </div>
    </BaseModal>
  </div>
</template>

<script>
import UploadComponent from "@/components/UploadComponent.vue";
import {
  getBulkRows,
  updateBulkRow,
  bulkUpdateRows,
  getBulkErrorSummary,
  downloadBulkRowsCsv,
  generateBulkCodes,
  validateBulkUpload,
  commitBulkUpload
} from "@/api/store/BulkProductsService.js";
import { getUploadedFiles } from "@/api/store/UploadFilesService.js";
import { getBrands } from "@/api/store/BrandsService.js";
import { loadPriceGroups } from "@/api/store/PriceGroupsService.js";

export default {
  name: "BulkProductUpload",
  components: { UploadComponent },
  data() {
    return {
      showUpload: false,
      uploads: [],
      loadingUploads: true,
      selectedUpload: null,
      rows: [],
      counts: null,
      loadingRows: false,
      activeFilter: null,
      page: 1,
      perPage: 100,
      pagination: null,
      errorSummary: [],
      errorFilter: null,
      downloadingCsv: false,
      validating: false,
      showGenerateCodes: false,
      codePrefix: "",
      codeStartFrom: 1,
      generatingCodes: false,
      bulkBrand: "",
      bulkDependencia: "",
      bulkPriceGroup: "",
      bulkCostIncludesDealerProfit: "",
      brands: [],
      priceGroups: []
    };
  },
  mounted() {
    this.refreshUploads();
    getBrands().then(res => { this.brands = res || []; });
    loadPriceGroups().then(res => { this.priceGroups = res || []; });
  },
  watch: {
    activeFilter() {
      if (this.selectedUpload) {
        this.page = 1;
        this.errorFilter = null;
        this.loadRows();
        if (this.activeFilter === false) this.loadErrorSummary();
      }
    },
    '$route.params.uploadId'(id) {
      if (id && (!this.selectedUpload || String(this.selectedUpload.id) !== String(id))) {
        const found = this.uploads.find(u => String(u.id) === String(id));
        if (found) this.openUpload(found);
      } else if (!id && this.selectedUpload) {
        this.selectedUpload = null;
      }
    }
  },
  methods: {
    refreshUploads() {
      this.loadingUploads = true;
      getUploadedFiles({ category: "bulk_product_create" })
        .then(res => {
          this.uploads = res;
          const routeId = this.$route.params.uploadId;
          if (routeId && !this.selectedUpload) {
            const found = res.find(u => String(u.id) === String(routeId));
            if (found) this.openUpload(found);
          }
        })
        .finally(() => {
          this.loadingUploads = false;
        });
    },
    onFileUploaded(upload) {
      if (!upload) return;
      this.uploads.unshift(upload);
      if (upload.category === 'bulk_product_create' && upload.id) {
        this.openUpload(upload);
      }
    },
    openUpload(upload) {
      this.selectedUpload = upload;
      this.activeFilter = null;
      this.page = 1;
      if (String(this.$route.params.uploadId) !== String(upload.id)) {
        this.$router.push({ name: 'BulkProductUpload', params: { uploadId: upload.id } });
      }
      this.loadRows();
    },
    loadRows() {
      this.loadingRows = true;
      // Paginado server-side: con archivos de miles de filas no se puede traer
      // (ni renderizar) todo el staging de una sola vez.
      getBulkRows(this.selectedUpload.id, this.activeFilter, this.page, this.perPage, this.errorFilter)
        .then(res => {
          this.rows = res.rows;
          this.counts = res.counts;
          this.pagination = res.pagination || null;
        })
        .finally(() => {
          this.loadingRows = false;
        });
    },
    goToPage(page) {
      if (page < 1 || (this.pagination && page > this.pagination.totalPages)) return;
      this.page = page;
      this.loadRows();
    },
    // Cada error viene como {error, detail} (o un string plano en filas viejas
    // pre-refactor) — el mensaje genérico no lleva el código para poder
    // agruparlo en el breakdown; el detalle específico de la fila se muestra
    // acá al lado, entre paréntesis.
    formatRowErrors(errors) {
      return errors
        .map(e => (typeof e === 'string' ? e : (e.detail ? `${e.error} (${e.detail})` : e.error)))
        .join(' · ');
    },
    loadErrorSummary() {
      getBulkErrorSummary(this.selectedUpload.id).then(res => {
        this.errorSummary = res.summary || [];
      });
    },
    filterByError(errorText) {
      this.errorFilter = this.errorFilter === errorText ? null : errorText;
      this.page = 1;
      this.loadRows();
    },
    clearErrorFilter() {
      this.errorFilter = null;
      this.page = 1;
      this.loadRows();
    },
    downloadCsv() {
      this.downloadingCsv = true;
      downloadBulkRowsCsv(this.selectedUpload.id, this.activeFilter)
        .catch(() => this.$toast.open({ message: "Error downloading CSV", type: "error", position: "top-right" }))
        .finally(() => { this.downloadingCsv = false; });
    },
    saveRow(row, field, value) {
      const payload = { [field]: value };
      updateBulkRow(row.id, payload).catch(() => {
        this.$toast.open({ message: "Error saving row", type: "error", position: "top-right" });
      });
    },
    revalidate() {
      this.validating = true;
      validateBulkUpload(this.selectedUpload.id)
        .then(res => {
          this.counts = res.counts;
          this.loadRows();
          if (this.activeFilter === false) this.loadErrorSummary();
          this.$toast.open({ message: "Validation complete", type: "success", position: "top-right" });
        })
        .catch(() => this.$toast.open({ message: "Error validating upload", type: "error", position: "top-right" }))
        .finally(() => { this.validating = false; });
    },
    openGenerateCodes() {
      this.codePrefix = "";
      this.codeStartFrom = 1;
      this.showGenerateCodes = true;
    },
    runGenerateCodes() {
      this.generatingCodes = true;
      generateBulkCodes(this.selectedUpload.id, this.codePrefix, this.codeStartFrom)
        .then(res => {
          this.showGenerateCodes = false;
          this.$toast.open({
            message: `${res.result.generated} codes generated. Last: ${res.result.lastCode}`,
            type: "success",
            position: "top-right"
          });
          this.loadRows();
        })
        .catch(() => {
          this.$toast.open({ message: "Error generating codes", type: "error", position: "top-right" });
        })
        .finally(() => {
          this.generatingCodes = false;
        });
    },
    // Los 4 métodos de abajo pegan contra /rows_bulk (todo el upload en el
    // servidor) en vez de iterar this.rows: desde que el grid pagina, this.rows
    // solo tiene la página visible, no las miles de filas del archivo.
    applyBulkBrand() {
      const brand = this.bulkBrand.trim();
      if (!brand) return;
      bulkUpdateRows(this.selectedUpload.id, 'brand', brand, this.activeFilter)
        .then(res => {
          this.$toast.open({ message: `Brand "${brand}" applied to ${res.updated} rows`, type: "success", position: "top-right" });
          this.loadRows();
        })
        .catch(() => this.$toast.open({ message: "Error updating rows", type: "error", position: "top-right" }));
    },
    applyBulkPriceGroup() {
      const price_group = this.bulkPriceGroup;
      if (!price_group) return;
      bulkUpdateRows(this.selectedUpload.id, 'price_group', price_group, this.activeFilter)
        .then(res => {
          this.$toast.open({ message: `Price Group "${price_group}" applied to ${res.updated} rows`, type: "success", position: "top-right" });
          this.loadRows();
        })
        .catch(() => this.$toast.open({ message: "Error updating rows", type: "error", position: "top-right" }));
    },
    applyBulkDependencia() {
      const dependencia = this.bulkDependencia;
      if (!dependencia) return;
      bulkUpdateRows(this.selectedUpload.id, 'dependencia', dependencia, this.activeFilter)
        .then(res => {
          this.$toast.open({ message: `Dependencia "${dependencia}" applied to ${res.updated} rows`, type: "success", position: "top-right" });
          this.loadRows();
        })
        .catch(() => this.$toast.open({ message: "Error updating rows", type: "error", position: "top-right" }));
    },
    applyBulkCostIncludesDealerProfit() {
      if (this.bulkCostIncludesDealerProfit === "") return;
      const cost_includes_dealer_profit = Number(this.bulkCostIncludesDealerProfit);
      // Same restriction as the per-row checkbox: only meaningful for LP45.
      // El filtro por price_group='LP45' solo se puede aplicar fila por fila en
      // el modelo actual de bulk_update_field (que solo filtra por is_valid), así
      // que acá seguimos iterando this.rows — pero avisamos que solo cubre la
      // página visible para no generar una falsa sensación de "aplicado a todo".
      const targetRows = this.rows.filter(row => row.priceGroup === 'LP45');
      if (!targetRows.length) {
        this.$toast.open({ message: "No LP45 rows on this page to update", type: "error", position: "top-right" });
        return;
      }
      Promise.all(targetRows.map(row => {
        row.costIncludesDealerProfit = cost_includes_dealer_profit;
        return updateBulkRow(row.id, { cost_includes_dealer_profit });
      }))
        .then(() => this.$toast.open({ message: `Cost incl. dealer profit applied to ${targetRows.length} LP45 rows on this page`, type: "success", position: "top-right" }))
        .catch(() => this.$toast.open({ message: "Error updating some rows", type: "error", position: "top-right" }));
    },
    commitUpload() {
      if (!confirm(`Commit ${this.counts.valid} valid products to the catalog?`)) return;
      commitBulkUpload(this.selectedUpload.id)
        .then(res => {
          this.$toast.open({
            message: `${res.result.created} products created. ${res.result.skipped} skipped.`,
            type: "success",
            position: "top-right"
          });
          this.loadRows();
        })
        .catch(() => {
          this.$toast.open({ message: "Error committing products", type: "error", position: "top-right" });
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.bulk-product-upload {
  padding: 20px;

  .page-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 20px;
    h2 { margin: 0; font-size: 20px; font-weight: 700; flex: 1; }
  }

  .loading-box { padding: 40px 0; display: flex; justify-content: center; }
  .empty-state { padding: 40px; text-align: center; color: #888; }

  .status-badge {
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 600;
    &.created   { background: #155724; color: #fff; }
    &.processed { background: #d4edda; color: #155724; }
    &.uploaded  { background: #cce5ff; color: #004085; }
    &.error     { background: #f8d7da; color: #721c24; }
  }

  .btn-sm { padding: 4px 10px; font-size: 12px; }

  .review-panel {
    .review-header {
      display: flex;
      align-items: center;
      gap: 10px;
      flex-wrap: wrap;
      margin-bottom: 16px;
      padding-bottom: 12px;
      border-bottom: 1px solid #ddd;

      .review-title { font-weight: 600; flex: 1; }

      .bulk-set-brand {
        display: flex;
        align-items: center;
        gap: 6px;
        .inline-input { width: 180px; }
      }
      .review-actions { display: flex; gap: 6px; margin-left: auto; }
    }

    .review-tabs {
      display: flex;
      align-items: center;
      gap: 4px;
      border-bottom: 2px solid #eee;
      margin-bottom: 14px;

      .review-tab {
        background: none;
        border: none;
        border-bottom: 2px solid transparent;
        margin-bottom: -2px;
        padding: 8px 14px;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        color: #666;

        .tab-count {
          display: inline-block;
          margin-left: 4px;
          padding: 1px 7px;
          border-radius: 10px;
          background: #eee;
          font-size: 11px;
        }

        &.active { color: #333; border-bottom-color: #333; }
        &.tab-valid.active   .tab-count { background: #d4edda; color: #155724; }
        &.tab-invalid.active .tab-count { background: #f8d7da; color: #721c24; }
      }

      .download-btn { margin-left: auto; }
    }

    .active-error-filter {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 12px;
      color: #555;
      background: #f4f4f4;
      border-radius: 4px;
      padding: 6px 10px;
      margin-bottom: 10px;
      strong { color: #333; }
    }

    .error-summary {
      background: #fdf3f3;
      border: 1px solid #f5c6cb;
      border-radius: 6px;
      padding: 10px 14px;
      margin-bottom: 14px;

      .error-summary-title {
        font-weight: 700;
        font-size: 13px;
        margin-bottom: 6px;
        color: #721c24;
        display: flex;
        align-items: center;
        gap: 10px;
      }

      .clear-filter-btn {
        background: none;
        border: none;
        color: #721c24;
        text-decoration: underline;
        font-size: 11px;
        cursor: pointer;
        padding: 0;
      }

      ul { list-style: none; margin: 0; padding: 0; max-height: 160px; overflow-y: auto; }
      li {
        font-size: 12px;
        padding: 3px 4px;
        color: #555;
        cursor: pointer;
        border-radius: 3px;
        &:hover { background: rgba(114, 28, 36, 0.08); }
        &.active { background: rgba(114, 28, 36, 0.15); font-weight: 600; color: #333; }
      }
      .error-count {
        display: inline-block;
        min-width: 24px;
        text-align: center;
        background: #721c24;
        color: #fff;
        border-radius: 10px;
        padding: 1px 6px;
        font-size: 11px;
        margin-right: 6px;
      }
    }

    .rows-table-wrap { overflow-x: auto; }

    .pagination-bar {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 14px;
      padding: 12px 0;
      font-size: 13px;
    }

    .rows-table {
      min-width: 900px;

      tr.row-invalid td:first-child { border-left: 3px solid #e74c3c; }
      tr.row-valid   td:first-child { border-left: 3px solid #27ae60; }

      .th-bulk {
        display: flex;
        align-items: center;
        gap: 4px;
        margin-top: 4px;
        .inline-input { width: 90px; font-size: 11px; padding: 2px 4px; color: #333; }
        .inline-select { font-size: 11px; padding: 2px 4px; color: #333; }
      }

      .btn-xs { padding: 2px 6px; font-size: 11px; }

      .inline-input {
        border: 1px solid #ddd;
        border-radius: 3px;
        padding: 3px 6px;
        font-size: 13px;
        width: 100%;
        min-width: 80px;
        color: #333;
        &.wide   { min-width: 160px; }
        &.narrow { min-width: 60px; width: 70px; }
      }

      .inline-select {
        border: 1px solid #ddd;
        border-radius: 3px;
        padding: 3px 4px;
        font-size: 13px;
        color: #333;
      }

      .pill-valid   { background: #d4edda; color: #155724; padding: 2px 7px; border-radius: 10px; font-size: 11px; }
      .pill-invalid { background: #f8d7da; color: #721c24; padding: 2px 7px; border-radius: 10px; font-size: 11px; }

      .errors-cell {
        max-width: 200px;
        .error-list { font-size: 11px; color: #e74c3c; }
      }
    }
  }

  ::v-deep(.generate-codes-modal .modal-content) {
    max-width: 600px;
  }

  .generate-modal {
    padding: 10px;
    min-width: 320px;
    h3 { margin-bottom: 8px; }
    p  { font-size: 13px; color: #666; margin-bottom: 16px; }

    .generate-form {
      display: grid;
      grid-template-columns: 120px 1fr;
      gap: 10px;
      align-items: center;
      margin-bottom: 14px;
      label { font-size: 13px; font-weight: 600; }
    }

    .preview {
      font-size: 13px;
      font-family: monospace;
      background: #f4f4f4;
      padding: 6px 10px;
      border-radius: 4px;
      margin-bottom: 14px;
    }
  }
}
</style>
